import type { ZodType } from "zod";
import { z as zod } from "zod";

import { decimal } from "./decimal.ts";
import { safeId } from "./safe-id.ts";
import {
  alphaNumeric,
  array,
  object,
  record,
  regexp,
  safeFileName,
  uuid,
} from "./schemas.ts";
import { string } from "./string.ts";
import "./readonly.ts";

interface DeprecatedForms {
  /**
   * @deprecated Write `schema.default(value)` instead.
   */
  readonly _default: typeof zod._default;
  /**
   * @deprecated Write `schema.catch(value)` instead.
   */
  readonly catch: typeof zod.catch;
  /**
   * @deprecated Write `schema.check(fn)` instead.
   */
  readonly check: typeof zod.check;
  /**
   * @deprecated Write `schema.decode(value)` instead.
   */
  readonly decode: typeof zod.decode;
  /**
   * @deprecated Write `schema.decodeAsync(value)` instead.
   */
  readonly decodeAsync: typeof zod.decodeAsync;
  /**
   * @deprecated Write the text in its object's `z.object()` map instead.
   */
  readonly describe: typeof zod.describe;
  /**
   * @deprecated Write `schema.encode(value)` instead.
   */
  readonly encode: typeof zod.encode;
  /**
   * @deprecated Write `schema.encodeAsync(value)` instead.
   */
  readonly encodeAsync: typeof zod.encodeAsync;
  /**
   * @deprecated Write `schema.endsWith(suffix)` instead.
   */
  readonly endsWith: typeof zod.endsWith;
  /**
   * @deprecated Write `schema.exactOptional()` instead.
   */
  readonly exactOptional: typeof zod.exactOptional;
  /**
   * @deprecated Write `schema.gt(value)` instead.
   */
  readonly gt: typeof zod.gt;
  /**
   * @deprecated Write `schema.gte(value)` instead.
   */
  readonly gte: typeof zod.gte;
  /**
   * @deprecated Write `schema.includes(value)` instead.
   */
  readonly includes: typeof zod.includes;
  /**
   * @deprecated Write `schema.keyof()` instead.
   */
  readonly keyof: typeof zod.keyof;
  /**
   * @deprecated Write `schema.length(value)` instead.
   */
  readonly length: typeof zod.length;
  /**
   * @deprecated Write `z.object(shape).catchall(z.unknown())` instead.
   */
  readonly looseObject: typeof zod.looseObject;
  /**
   * @deprecated Write `schema.lowercase()` instead.
   */
  readonly lowercase: typeof zod.lowercase;
  /**
   * @deprecated Write `schema.lt(value)` instead.
   */
  readonly lt: typeof zod.lt;
  /**
   * @deprecated Write `schema.lte(value)` instead.
   */
  readonly lte: typeof zod.lte;
  /**
   * @deprecated Write `schema.max(value)` instead.
   */
  readonly maxLength: typeof zod.maxLength;
  /**
   * @deprecated Write `schema.max(value)` instead.
   */
  readonly maxSize: typeof zod.maxSize;
  /**
   * @deprecated Write the text in its object's `z.object()` map instead.
   */
  readonly meta: typeof zod.meta;
  /**
   * @deprecated Write `schema.mime(types)` instead.
   */
  readonly mime: typeof zod.mime;
  /**
   * @deprecated Write `schema.min(value)` instead.
   */
  readonly minLength: typeof zod.minLength;
  /**
   * @deprecated Write `schema.min(value)` instead.
   */
  readonly minSize: typeof zod.minSize;
  /**
   * @deprecated Write `schema.multipleOf(value)` instead.
   */
  readonly multipleOf: typeof zod.multipleOf;
  /**
   * @deprecated Write `schema.negative()` instead.
   */
  readonly negative: typeof zod.negative;
  /**
   * @deprecated Write `schema.nonnegative()` instead.
   */
  readonly nonnegative: typeof zod.nonnegative;
  /**
   * @deprecated Write `schema.nonoptional()` instead.
   */
  readonly nonoptional: typeof zod.nonoptional;
  /**
   * @deprecated Write `schema.nonpositive()` instead.
   */
  readonly nonpositive: typeof zod.nonpositive;
  /**
   * @deprecated Write `schema.normalize()` instead.
   */
  readonly normalize: typeof zod.normalize;
  /**
   * @deprecated Write `schema.nullable()` instead.
   */
  readonly nullable: typeof zod.nullable;
  /**
   * @deprecated Write `schema.nullish()` instead.
   */
  readonly nullish: typeof zod.nullish;
  /**
   * @deprecated Write `schema.optional()` instead.
   */
  readonly optional: typeof zod.optional;
  /**
   * @deprecated Write `schema.overwrite(fn)` instead.
   */
  readonly overwrite: typeof zod.overwrite;
  /**
   * @deprecated Write `schema.parse(value)` instead.
   */
  readonly parse: typeof zod.parse;
  /**
   * @deprecated Write `schema.parseAsync(value)` instead.
   */
  readonly parseAsync: typeof zod.parseAsync;
  /**
   * @deprecated Write `schema.pipe(target)` instead.
   */
  readonly pipe: typeof zod.pipe;
  /**
   * @deprecated Write `schema.positive()` instead.
   */
  readonly positive: typeof zod.positive;
  /**
   * @deprecated Write `schema.prefault(value)` instead.
   */
  readonly prefault: typeof zod.prefault;
  /**
   * @deprecated Write `schema.readonly()` instead.
   */
  readonly readonly: typeof zod.readonly;
  /**
   * @deprecated Write `schema.refine(fn)` instead.
   */
  readonly refine: typeof zod.refine;
  /**
   * @deprecated Write `schema.regex(pattern)` instead.
   */
  readonly regex: typeof zod.regex;
  /**
   * @deprecated Write `schema.safeDecode(value)` instead.
   */
  readonly safeDecode: typeof zod.safeDecode;
  /**
   * @deprecated Write `schema.safeDecodeAsync(value)` instead.
   */
  readonly safeDecodeAsync: typeof zod.safeDecodeAsync;
  /**
   * @deprecated Write `schema.safeEncode(value)` instead.
   */
  readonly safeEncode: typeof zod.safeEncode;
  /**
   * @deprecated Write `schema.safeEncodeAsync(value)` instead.
   */
  readonly safeEncodeAsync: typeof zod.safeEncodeAsync;
  /**
   * @deprecated Write `schema.safeParse(value)` instead.
   */
  readonly safeParse: typeof zod.safeParse;
  /**
   * @deprecated Write `schema.safeParseAsync(value)` instead.
   */
  readonly safeParseAsync: typeof zod.safeParseAsync;
  /**
   * @deprecated Write `schema.size(value)` instead.
   */
  readonly size: typeof zod.size;
  /**
   * @deprecated Write `schema.slugify()` instead.
   */
  readonly slugify: typeof zod.slugify;
  /**
   * @deprecated Write `schema.startsWith(prefix)` instead.
   */
  readonly startsWith: typeof zod.startsWith;
  /**
   * @deprecated Write `z.object(shape).strict()` instead.
   */
  readonly strictObject: typeof zod.strictObject;
  /**
   * @deprecated Write `schema.superRefine(fn)` instead.
   */
  readonly superRefine: typeof zod.superRefine;
  /**
   * @deprecated Write `schema.toLowerCase()` instead.
   */
  readonly toLowerCase: typeof zod.toLowerCase;
  /**
   * @deprecated Write `schema.toUpperCase()` instead.
   */
  readonly toUpperCase: typeof zod.toUpperCase;
  /**
   * @deprecated Write `schema.transform(fn)` instead.
   */
  readonly transform: typeof zod.transform;
  /**
   * @deprecated Write `schema.trim()` instead.
   */
  readonly trim: typeof zod.trim;
  /**
   * @deprecated Write `schema.uppercase()` instead.
   */
  readonly uppercase: typeof zod.uppercase;
  /**
   * @deprecated Write `schema.validate(value)` instead.
   */
  readonly validate: typeof zod.validate;
  /**
   * @deprecated Write `schema.validateAsync(value)` instead.
   */
  readonly validateAsync: typeof zod.validateAsync;
}

export const z: {
  readonly alphaNumeric: typeof alphaNumeric;
  readonly array: typeof array;
  readonly decimal: typeof decimal;
  readonly object: typeof object;
  readonly record: typeof record;
  readonly regexp: typeof regexp;
  readonly safeFileName: typeof safeFileName;
  readonly safeId: typeof safeId;
  readonly string: typeof string;
  readonly uuid: typeof uuid;
} & DeprecatedForms &
  Omit<
    typeof zod,
    "array" | "object" | "record" | "string" | "uuid" | keyof DeprecatedForms
  > = {
  ...zod,
  alphaNumeric,
  array,
  decimal,
  object,
  record,
  regexp,
  safeFileName,
  safeId,
  string,
  uuid,
};

// oxlint-disable-next-line @typescript-eslint/no-namespace
export namespace z {
  export type infer<T extends ZodType> = zod.infer<T>;

  export type input<T extends ZodType> = zod.input<T>;
}
